import { Package } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { formatGiftValue } from "@/lib/format-gift-value"
import type { Locale } from "@/types"
import type { CollabOfferedProduct } from "@/types/collab"

/**
 * OKS-990 — the read-only offer list on the mission detail (UX Spec §S2).
 *
 * One compact row per offered product: thumbnail, name, value, a variant-count
 * line when there is a real choice, and an "out of stock" badge when the
 * product is exhausted.
 *
 * # ⚠️ NOTHING HERE IS SELECTABLE, and that is a correctness rule not a style
 *
 * PRD R3 puts the choice inside the request flow, not in the display. This
 * same gift section also renders on `expired`, `declined` and `completed`
 * collabs (OKS-986), where a live selector would be a lie — it would offer an
 * action the state machine refuses. The picker opens from the primary CTA and
 * closes with the grant; this states the offer and nothing more.
 *
 * # ⚠️ An exhausted product stays VISIBLE (UX-A4)
 *
 * A silently shorter list reads as a smaller offer. A badged one reads as an
 * honest offer, and it is what makes "a run on one shade never blocks
 * participation via another" legible before the creator taps anything.
 *
 * # ⚠️ No "see all N" disclosure, even at N > 5 (UX-A3)
 *
 * The offer IS the persuasion. Hiding four fifths of it to save vertical space
 * on a page that already scrolls is the wrong trade.
 */
export function OfferList({
  offer,
  locale,
}: {
  offer: CollabOfferedProduct[]
  locale: Locale
}) {
  const { t } = useTranslation()
  if (offer.length === 0) return null

  return (
    <ul
      className="flex flex-col gap-2"
      data-testid="mission-detail-offer-list"
      // The list is a description of the offer, not a menu. Screen readers get
      // a plain list; the choosing happens in the dialog, which is a radiogroup.
      aria-label={t("collabs.detail_offer.list_label", { count: offer.length })}
    >
      {offer.map((product) => (
        <li
          key={product.id}
          className="flex min-w-0 items-center gap-3 rounded-md border p-2"
          data-testid="mission-detail-offer-item"
          data-exhausted={product.exhausted ? "true" : "false"}
        >
          <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md">
            {product.photo_url ? (
              <img
                src={product.photo_url}
                /* The name sits beside it — announcing both double-reads. */
                alt=""
                className="size-full object-cover"
              />
            ) : (
              <Package className="size-4" aria-hidden="true" />
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* ⚠️ WRAPS, never truncates — the name is the identifying
                information (UX §5). The card truncates because it has one line;
                this page does not. */}
            <span className="text-sm font-medium wrap-anywhere">
              {product.name}
            </span>
            {product.variant_count >= 2 && (
              <span className="text-muted-foreground text-xs">
                {t("collabs.detail_offer.variants", {
                  count: product.variant_count,
                })}
              </span>
            )}
          </div>

          {/* Value and badge both `shrink-0`, so the squeeze at 320px lands on
              the name, which wraps, rather than on the tokens that cannot. */}
          <OfferValue product={product} locale={locale} />
          {product.exhausted && (
            <Badge
              variant="destructive"
              className="shrink-0"
              data-testid="mission-detail-offer-item-exhausted"
            >
              {t("collabs.detail_offer.exhausted")}
            </Badge>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * OKS-1000 — one offered row's value token, or nothing.
 *
 * ⚠️ Renders NOTHING at `value_source: "none"` — no empty span, no dash, no
 * "0 €". The row keeps its photo, name, variant-count line and exhausted
 * badge; only the value is absent. A `none` + exhausted row is a real shape.
 *
 * ⚠️ `shrink-0` like the badge beside it, so the squeeze at 320px lands on the
 * name, which wraps, rather than on the two tokens that cannot.
 */
function OfferValue({
  product,
  locale,
}: {
  product: CollabOfferedProduct
  locale: Locale
}) {
  const { t } = useTranslation()
  if (product.value_source === "none") return null

  const value = formatGiftValue(
    product.effective_value_cents,
    product.effective_currency,
    locale,
  )
  if (value == null) return null

  return (
    <span
      className="text-primary shrink-0 text-xs font-semibold"
      data-testid="mission-detail-offer-item-value"
      data-value-source={product.value_source}
    >
      {product.value_is_range
        ? t("collabs.detail_offer.value_from", { value })
        : value}
    </span>
  )
}
